import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type DragEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { ApiError } from '../api/client';
import type { AppointmentStatus, CalendarItem, CalendarSlot } from '../api/types';
import { useDialog } from '../components/dialogContext';
import { ErrorMessage } from '../components/ErrorMessage';
import { Icon } from '../components/icons';
import { PageLoader } from '../components/Loader';
import { addDays, appointmentStatusLabels, fmtDate, fmtDateTime, fmtTime, fromLocal, localDate, travelLabel } from '../ops/format';
import { dragType, movable, seriesOf } from '../ops/calendarColors';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

type View = 'day' | 'week' | 'month';
const weekdays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const weekday = (date: string) => (new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7;

function range(view: View, date: string): { from: string; to: string; days: string[] } {
  if (view === 'day') return { from: date, to: addDays(date, 1), days: [date] };
  if (view === 'week') {
    const monday = addDays(date, -weekday(date));
    const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
    return { from: monday, to: addDays(monday, 7), days };
  }
  const first = `${date.slice(0, 7)}-01`;
  const start = addDays(first, -weekday(first));
  const [y = 1970, m = 1] = first.split('-').map(Number);
  const nextMonth = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const days: string[] = [];
  for (let d = start; d < nextMonth || weekday(d) !== 0; d = addDays(d, 1)) days.push(d);
  return { from: first, to: nextMonth, days };
}

// CalendarPage: interner Kalender (Termine sind führend; Outlook wird synchronisiert).
// ownUserId: Makler-Sicht (B-28) – nur die eigenen Termine, ohne Auswahl von Außendienst, Gebiet und Landkreis.
export function CalendarPage({ embedded = false, ownUserId }: { embedded?: boolean; ownUserId?: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const dialog = useDialog();
  const [dropDay, setDropDay] = useState<string>();
  const [params, setParams] = useSearchParams();
  const view = (params.get('view') as View | null) ?? 'week';
  const date = params.get('date') ?? localDate();
  const userId = ownUserId ?? params.get('user') ?? '';
  const areaId = params.get('area') ?? '';
  const status = params.get('status') ?? '';
  const county = (params.get('county') ?? '').toLowerCase();
  const r = range(view, date);
  const q = { from: fromLocal(r.from, '00:00'), to: fromLocal(r.to, '00:00'), user_id: userId || undefined, area_id: areaId || undefined, status: status ? [status] : undefined };

  const calendar = useQuery({ queryKey: projectKeys.calendar(project.id, q), queryFn: ({ signal }) => papi.calendar(q, signal), placeholderData: (prev) => prev });
  const members = useQuery({ queryKey: projectKeys.members(project.id), queryFn: ({ signal }) => papi.members(signal), staleTime: 5 * 60_000 });
  const areas = useQuery({ queryKey: [...projectKeys.areas(project.id), 'active'], queryFn: ({ signal }) => papi.projectAreas(false, signal), staleTime: 5 * 60_000 });
  const fieldSales = (members.data?.items ?? []).filter((m) => m.role_key === 'FIELD_SALES' && m.status === 'ACTIVE');
  // Tagesansicht eines Außendienstes: geschätzte Fahrzeit vor jedem Termin (aus der Tagesroute).
  const route = useQuery({
    queryKey: projectKeys.route(project.id, date, userId),
    queryFn: ({ signal }) => papi.fieldSalesRoute(date, userId, signal),
    enabled: view === 'day' && !!userId,
  });
  const travel = new Map((route.data?.stops ?? []).map((s) => [s.appointment_id, s.travel_minutes]));
  const hints = new Map<string, string[]>();
  for (const h of route.data?.hints ?? []) hints.set(h.stop_id, [...(hints.get(h.stop_id) ?? []), h.message]);

  // Farbe je Makler (Reihenfolge der Mitglieder), Legende nur für Makler mit Terminen im Ausschnitt.
  const series = seriesOf(fieldSales.map((m) => m.user_id));

  // Termine per Ziehen auf einen anderen Tag verschieben (gleiche Uhrzeit, mit Bestätigung; Recht appointment.reschedule).
  const canPlan = can('appointment.reschedule');
  const move = useMutation({
    mutationFn: (m: { id: string; startAt: string; allow?: boolean }) =>
      papi.rescheduleAppointment(m.id, { start_at: m.startAt, reason: 'Im Kalender verschoben', allow_conflicts: m.allow || undefined }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) }),
  });
  const dropOn = async (day: string, e: DragEvent) => {
    e.preventDefault();
    setDropDay(undefined);
    const it = (calendar.data?.items ?? []).find((i) => i.id === e.dataTransfer.getData(dragType));
    if (!it || localDate(new Date(it.start_at)) === day) return;
    const startAt = fromLocal(day, fmtTime(it.start_at));
    const ok = await dialog.confirm({
      title: 'Termin verschieben?',
      message: `${it.lead_name ?? it.title}: von ${fmtDateTime(it.start_at)} auf ${fmtDateTime(startAt)} (${it.field_sales_name ?? 'ohne Makler'}). Makler und Outlook werden aktualisiert.`,
      confirmLabel: 'Verschieben',
    });
    if (!ok) return;
    try {
      await move.mutateAsync({ id: it.id, startAt });
    } catch (err) {
      if (!(err instanceof ApiError && err.code === 'appointment_conflict')) return;
      const again = await dialog.confirm({ title: 'Konflikt im Kalender', message: err.message, confirmLabel: 'Trotzdem verschieben' });
      if (again) await move.mutateAsync({ id: it.id, startAt, allow: true }).catch(() => undefined);
    }
  };
  const dropProps = (day: string) =>
    canPlan
      ? {
          onDragOver: (e: DragEvent) => {
            if (!e.dataTransfer.types.includes(dragType)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (dropDay !== day) setDropDay(day);
          },
          onDragLeave: () => setDropDay((d) => (d === day ? undefined : d)),
          onDrop: (e: DragEvent) => void dropOn(day, e),
        }
      : {};

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };
  const step = (dir: number) => set('date', view === 'day' ? addDays(date, dir) : view === 'week' ? addDays(date, 7 * dir) : addDays(`${date.slice(0, 7)}-15`, 30 * dir));
  const items = (calendar.data?.items ?? []).filter((i) => !county || (i.county_name ?? '').toLowerCase().includes(county));
  const byDay = new Map<string, CalendarItem[]>();
  for (const it of items) {
    const d = localDate(new Date(it.start_at));
    byDay.set(d, [...(byDay.get(d) ?? []), it]);
  }
  const busyByDay = new Map<string, CalendarSlot[]>();
  for (const s of calendar.data?.external ?? []) {
    const d = localDate(new Date(s.start_at));
    busyByDay.set(d, [...(busyByDay.get(d) ?? []), s]);
  }

  return (
    <div className={embedded ? 'embedded-page calendar-page' : 'page calendar-page'}>
      <header className="page-header">
        {!embedded && <h1>Kalender</h1>}
        <div className="chips" role="group" aria-label="Ansicht">
          {(['day', 'week', 'month'] as View[]).map((v) => (
            <button key={v} type="button" className={view === v ? 'chip active' : 'chip'} aria-pressed={view === v} onClick={() => set('view', v)}>
              {v === 'day' ? 'Tag' : v === 'week' ? 'Woche' : 'Monat'}
            </button>
          ))}
        </div>
        <div className="calendar-nav">
          <button type="button" className="btn btn-small" onClick={() => step(-1)} aria-label="Zurück">
            ‹
          </button>
          <button type="button" className="btn btn-small" onClick={() => set('date', localDate())}>
            Heute
          </button>
          <button type="button" className="btn btn-small" onClick={() => step(1)} aria-label="Weiter">
            ›
          </button>
          <strong>{view === 'month' ? new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(new Date(`${date}T12:00:00Z`)) : `${fmtDate(new Date(`${r.from}T12:00:00Z`))}${view === 'week' ? ` – ${fmtDate(new Date(`${addDays(r.to, -1)}T12:00:00Z`))}` : ''}`}</strong>
        </div>
      </header>
      <div className="filters-row">
        {!ownUserId && (
          <>
        <label className="field">
          <span>Außendienst</span>
          <select value={userId} onChange={(e) => set('user', e.target.value)}>
            <option value="">Alle</option>
            {fieldSales.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.first_name} {m.last_name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Gebiet</span>
          <select value={areaId} onChange={(e) => set('area', e.target.value)}>
            <option value="">Alle</option>
            {(areas.data?.items ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Landkreis</span>
          <input value={params.get('county') ?? ''} onChange={(e) => set('county', e.target.value)} placeholder="z. B. Böblingen" />
        </label>
          </>
        )}
        <label className="field">
          <span>Status</span>
          <select value={status} onChange={(e) => set('status', e.target.value)}>
            <option value="">Alle</option>
            {(Object.keys(appointmentStatusLabels) as AppointmentStatus[]).map((s) => (
              <option key={s} value={s}>
                {appointmentStatusLabels[s]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!ownUserId && <CalendarLegend items={items} series={series} />}
      {canPlan && <p className="muted small">Termine lassen sich per Ziehen auf einen anderen Tag verschieben (gleiche Uhrzeit, mit Bestätigung).</p>}
      {move.isError && !(move.error instanceof ApiError && move.error.code === 'appointment_conflict') && <p className="alert alert-error">{move.error.message}</p>}
      {calendar.isPending ? (
        <PageLoader label="Kalender wird geladen …" />
      ) : calendar.isError ? (
        <ErrorMessage error={calendar.error} onRetry={() => void calendar.refetch()} />
      ) : (
        <>
          {calendar.data.truncated && <p className="alert alert-info">Es werden nur die ersten Termine angezeigt – bitte Filter verwenden.</p>}
          {view === 'month' ? (
            <div className="month-grid" role="grid" aria-label="Monatsansicht">
              {weekdays.map((w) => (
                <div key={w} className="month-head" role="columnheader">
                  {w}
                </div>
              ))}
              {r.days.map((d) => {
                const list = byDay.get(d) ?? [];
                return (
                  <div
                    key={d}
                    className={[d.slice(0, 7) === date.slice(0, 7) ? 'month-cell' : 'month-cell other-month', dropDay === d && 'drop-target'].filter(Boolean).join(' ')}
                    role="gridcell"
                    {...dropProps(d)}
                  >
                    <button type="button" className="day-number" onClick={() => setParams({ ...Object.fromEntries(params), view: 'day', date: d })}>
                      {Number(d.slice(8))}
                    </button>
                    {list.slice(0, 3).map((it) => (
                      <AppointmentChip key={it.id} item={it} projectKey={project.key} compact series={series.get(it.field_sales_user_id ?? '')} draggable={canPlan} />
                    ))}
                    {list.length > 3 && <span className="muted">+{list.length - 3} weitere</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={view === 'week' ? 'week-grid' : 'day-list'}>
              {r.days.map((d) => (
                <section key={d} className={dropDay === d ? 'day-column drop-target' : 'day-column'} aria-label={fmtDate(new Date(`${d}T12:00:00Z`))} {...dropProps(d)}>
                  <h2 className={d === localDate() ? 'today' : undefined}>{fmtDate(new Date(`${d}T12:00:00Z`))}</h2>
                  {(byDay.get(d) ?? []).length === 0 && (busyByDay.get(d) ?? []).length === 0 && <p className="muted">Keine Termine</p>}
                  {view === 'day' && !userId && (byDay.get(d) ?? []).length > 1 && (
                    <p className="muted">Für Fahrzeiten einen Außendienst auswählen.</p>
                  )}
                  {(byDay.get(d) ?? []).map((it) => (
                    <AppointmentChip
                      key={it.id}
                      item={it}
                      projectKey={project.key}
                      travel={travel.get(it.id)}
                      hints={hints.get(it.id)}
                      series={series.get(it.field_sales_user_id ?? '')}
                      draggable={canPlan}
                    />
                  ))}
                  {(busyByDay.get(d) ?? []).map((s) => (
                    <div key={`${s.start_at}-${s.title}`} className="busy-slot">
                      {fmtTime(s.start_at)}–{fmtTime(s.end_at)} Belegt (Outlook)
                    </div>
                  ))}
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// CalendarLegend: Makler mit Terminen im Ausschnitt und ihre Farbe.
function CalendarLegend({ items, series }: { items: CalendarItem[]; series: Map<string, number> }) {
  const names = new Map<string, string>();
  for (const it of items) if (it.field_sales_user_id && it.field_sales_name) names.set(it.field_sales_user_id, it.field_sales_name);
  if (names.size === 0) return null;
  return (
    <ul className="calendar-legend" aria-label="Farben je Makler">
      {[...names].map(([id, name]) => (
        <li key={id}>
          <span className={`legend-swatch series-${series.get(id) ?? 0}`} aria-hidden="true" /> {name}
        </li>
      ))}
    </ul>
  );
}

function AppointmentChip({
  item,
  projectKey,
  compact,
  travel,
  hints,
  series,
  draggable,
}: {
  item: CalendarItem;
  projectKey: string;
  compact?: boolean;
  travel?: number | null;
  hints?: string[];
  series?: number;
  draggable?: boolean;
}) {
  const syncProblem = item.sync_status === 'FAILED' || item.sync_status === 'CONFLICT';
  const canDrag = !!draggable && movable(item.status);
  return (
    <Link
      to={`/p/${projectKey}/termine/${item.id}`}
      className={`appt-chip status-${item.status.toLowerCase()} series-${series ?? 0}${canDrag ? ' draggable' : ''}`}
      draggable={canDrag}
      onDragStart={
        canDrag
          ? (e) => {
              e.dataTransfer.setData(dragType, item.id);
              e.dataTransfer.effectAllowed = 'move';
            }
          : undefined
      }
    >
      {travel != null && <span className="travel-before">Fahrzeit ca. {travelLabel(travel)}</span>}
      <strong>{fmtTime(item.start_at)}</strong> {item.lead_name ?? item.title}
      {!compact && (
        <span className="muted block">
          {[item.city, item.field_sales_name].filter(Boolean).join(' · ')}
          {item.status !== 'PLANNED' && ` · ${appointmentStatusLabels[item.status]}`}
        </span>
      )}
      {syncProblem && <span className="badge badge-warn">Outlook</span>}
      {item.handback_priority && (
        <span className={item.handback_priority === 'URGENT' ? 'badge badge-error' : 'badge badge-warn'} title="Vom Makler an die Terminplanung zurückgegeben">
          Rückgabe
        </span>
      )}
      {hints?.map((h) => (
        <span key={h} className="chip-hint">
          <Icon name="warning" size={14} /> {h}
        </span>
      ))}
    </Link>
  );
}
